import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { LuChevronLeft } from 'react-icons/lu';
import { FiLinkedin } from 'react-icons/fi';
import api from '../../../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatTimestamp = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('en-US', {
    month:   'short',
    day:     'numeric',
    hour:    'numeric',
    minute:  '2-digit',
    hour12:  true,
  });
};

const stripLinkedInHandle = (url = '') =>
  url
    .replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, '')
    .replace(/\/$/, '');

// ── LinkedInConversationView ──────────────────────────────────────────────────

const LinkedInConversationView = ({ conversation, onBack }) => {
  const [messages,  setMessages]  = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!conversation?.heyreach_conversation_id) return;
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get(
          `/api/integrations/heyreach/conversations/${conversation.heyreach_conversation_id}/messages`
        );
        if (!cancelled) setMessages(data.messages || []);
      } catch {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [conversation?.heyreach_conversation_id]);

  return (
    <Box flex={1} display="flex" flexDirection="column" overflow="hidden" minH={0}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <HStack
        px={3}
        py={2.5}
        borderBottom="1px solid rgba(255,255,255,0.08)"
        spacing={2}
        flexShrink={0}
      >
        <IconButton
          icon={<LuChevronLeft size={16} />}
          onClick={onBack}
          variant="ghost"
          size="xs"
          color="rgba(255,255,255,0.5)"
          aria-label="Back to conversations"
          _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
          _focus={{ boxShadow: 'none' }}
        />

        <VStack align="start" spacing={0} flex={1} minW={0}>
          <Text color="#fafafa" fontSize="xs" fontWeight="600" noOfLines={1}>
            {conversation.lead_name || 'Lead'}
          </Text>
          {conversation.lead_linkedin_url && (
            <HStack spacing={1}>
              <Box color="rgba(10,102,194,0.7)" flexShrink={0}>
                <FiLinkedin size={9} />
              </Box>
              <Text
                as="a"
                href={conversation.lead_linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                color="rgba(10,102,194,0.75)"
                fontSize="10px"
                noOfLines={1}
                _hover={{ color: 'rgba(10,102,194,1)' }}
              >
                {stripLinkedInHandle(conversation.lead_linkedin_url)}
              </Text>
            </HStack>
          )}
        </VStack>

        {conversation.li_sender_name && (
          <Text
            color="rgba(255,255,255,0.2)"
            fontSize="10px"
            flexShrink={0}
            noOfLines={1}
            maxW="80px"
            textAlign="right"
          >
            via {conversation.li_sender_name}
          </Text>
        )}
      </HStack>

      {/* ── Message thread ─────────────────────────────────────────────── */}
      <Box
        flex={1}
        overflowY="auto"
        px={4}
        py={3}
        display="flex"
        flexDirection="column"
        gap={3}
        minH={0}
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bg: 'rgba(255,255,255,0.08)', borderRadius: '2px' },
        }}
      >
        {isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1} pt={8}>
            <Spinner size="sm" color="rgba(255,255,255,0.4)" />
          </Box>
        ) : messages.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1} pt={8}>
            <Text color="rgba(255,255,255,0.25)" fontSize="xs">No messages to display</Text>
          </Box>
        ) : (
          messages.map((msg, idx) => {
            const isOutbound = msg.direction === 'outbound';
            return (
              <Box
                key={msg.heyreach_message_id || idx}
                display="flex"
                flexDirection="column"
                alignItems={isOutbound ? 'flex-end' : 'flex-start'}
              >
                <Box
                  maxW="82%"
                  px={3}
                  py={2}
                  borderRadius={isOutbound ? '12px 12px 3px 12px' : '12px 12px 12px 3px'}
                  bg={isOutbound ? 'rgba(255,255,255,0.07)' : 'rgba(10,102,194,0.12)'}
                  border={`1px solid ${isOutbound ? 'rgba(255,255,255,0.10)' : 'rgba(10,102,194,0.22)'}`}
                >
                  <Text
                    color={isOutbound ? 'rgba(255,255,255,0.75)' : '#fafafa'}
                    fontSize="xs"
                    lineHeight="1.65"
                    whiteSpace="pre-wrap"
                  >
                    {msg.content}
                  </Text>
                </Box>
                <Text color="rgba(255,255,255,0.2)" fontSize="9px" mt={1} px={0.5}>
                  {formatTimestamp(msg.sent_at)}
                </Text>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default LinkedInConversationView;
